import React, { useMemo } from 'react';
import { Card, Row, Col, List, Typography, Divider, Collapse } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  BulbOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import NinePalaceFlyingStar from './NinePalaceFlyingStar';
import AirflowAssessment from './AirflowAssessment';
import {
  AirflowAnalysisResult,
  CommentaryFocus,
  ImageAnalysisResult,
  FengShuiAnalysisResult,
} from '../types';
import { buildHouseLuckChart, PalaceKey } from '../utils/houseLuckChart';

const { Title, Paragraph, Text } = Typography;

interface AnalysisResultProps {
  imageAnalysis: ImageAnalysisResult;
  fengShuiAnalysis: FengShuiAnalysisResult;
  airflowAnalysis?: AirflowAnalysisResult;
  previewUrl: string;
  houseYear: number;
  annualYear: number;
  sitting: PalaceKey;
  facing: PalaceKey;
  sittingAngle: number;
  facingAngle: number;
}

const splitAnnualOrientationComment = (comment: string, annualYear: number) => {
  const text = comment.trim();
  if (!text) {
    return { orientationText: '', annualText: '' };
  }

  const escapedYear = String(annualYear).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const annualPattern = new RegExp(`${escapedYear}\\s*年\\s*流年|${escapedYear}\\s*年`);
  const match = text.match(annualPattern);

  if (!match || typeof match.index !== 'number') {
    return { orientationText: text, annualText: '' };
  }

  return {
    orientationText: text.slice(0, match.index).trim(),
    annualText: text.slice(match.index).trim(),
  };
};

const splitMarkdownHighlights = (text: string) => {
  const parts: Array<{ text: string; highlighted: boolean }> = [];
  const pattern = /\*\*([^*]+)\*\*/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) {
      parts.push({ text: text.slice(cursor, match.index), highlighted: false });
    }
    parts.push({ text: match[1], highlighted: true });
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), highlighted: false });
  }

  return parts;
};

const splitLegacyImageryHighlights = (text: string) => {
  const parts: Array<{ text: string; highlighted: boolean }> = [];
  const pattern = /(?:取[象像]|象主)[^。；;，,]*/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) {
      parts.push({ text: text.slice(cursor, match.index), highlighted: false });
    }
    parts.push({ text: match[0], highlighted: true });
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), highlighted: false });
  }

  return parts.length ? parts : [{ text, highlighted: false }];
};

const splitHighlightedText = (text: string) => {
  const markdownParts = splitMarkdownHighlights(text);
  return markdownParts.some((part) => part.highlighted)
    ? markdownParts
    : splitLegacyImageryHighlights(text);
};

const renderHighlightedImagery = (text: string) => (
  splitHighlightedText(text).map((part, index) => (
    <span
      key={`${part.highlighted ? 'imagery' : 'text'}-${index}`}
      className={part.highlighted ? 'imagery-highlight' : undefined}
    >
      {part.text}
    </span>
  ))
);

function CommentaryFocusPanel({ focus }: { focus?: CommentaryFocus }) {
  const imagery = focus?.imagery?.filter(Boolean) ?? [];
  const cautions = focus?.cautions?.filter(Boolean) ?? [];

  if (!imagery.length && !cautions.length) {
    return null;
  }

  return (
    <div className="commentary-focus-grid">
      {imagery.length ? (
        <div className="commentary-focus-card commentary-focus-imagery">
          <h5>取像重点</h5>
          <List
            size="small"
            dataSource={imagery}
            renderItem={(item) => <List.Item>• {renderHighlightedImagery(item)}</List.Item>}
          />
        </div>
      ) : null}
      {cautions.length ? (
        <div className="commentary-focus-card commentary-focus-cautions">
          <h5>注意事项</h5>
          <List
            size="small"
            dataSource={cautions}
            renderItem={(item) => <List.Item>• {renderHighlightedImagery(item)}</List.Item>}
          />
        </div>
      ) : null}
    </div>
  );
}

function CollapsibleComment({
  title,
  text,
}: {
  title: string;
  text: string;
}) {
  return (
    <Collapse
      className="commentary-body-collapse"
      ghost
      size="small"
      items={[
        {
          key: title,
          label: title,
          children: <Paragraph>{renderHighlightedImagery(text)}</Paragraph>,
        },
      ]}
    />
  );
}

/**
 * 分析结果展示组件
 */
const AnalysisResult: React.FC<AnalysisResultProps> = ({
  imageAnalysis,
  fengShuiAnalysis,
  airflowAnalysis,
  previewUrl,
  houseYear,
  annualYear,
  sitting,
  facing,
  sittingAngle,
  facingAngle,
}) => {
  const planImageUrl = imageAnalysis.yoloAnalysis?.visualizationImage ?? previewUrl;
  const chart = useMemo(
    () => buildHouseLuckChart(houseYear, annualYear, sitting, facing, sittingAngle, facingAngle),
    [annualYear, facing, facingAngle, houseYear, sitting, sittingAngle],
  );
  const orientationComment = splitAnnualOrientationComment(
    fengShuiAnalysis.orientationComment || '',
    annualYear,
  );

  // 根据分数获取颜色
  const getScoreColor = (score: number) => {
    if (score >= 80) return '#3f7b58';
    if (score >= 60) return '#b67a24';
    return '#b94a3a';
  };
  const formatGuidanceList = (value: unknown) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean).join('、');
    }
    if (typeof value === 'string') {
      return value;
    }
    return '暂无明确项';
  };

  return (
    <div className="result-section">
      <div className="result-heading">
        <span className="section-kicker">Analysis report</span>
        <Title level={2}>空间判断结果</Title>
        <Paragraph>结合图像识别结果与阳宅方位逻辑，先看整体评分，再进入具体格局和调整建议。</Paragraph>
      </div>

      <Row gutter={[24, 24]} className="result-grid">
      {/* 宅运盘模块 */}
      <Col xs={24} lg={12}>
        <Card className="result-card nine-palace-card" title="宅运盘" bordered={false}>
          <NinePalaceFlyingStar
            houseYear={houseYear}
            annualYear={annualYear}
            sitting={sitting}
            facing={facing}
            sittingAngle={sittingAngle}
            facingAngle={facingAngle}
            planImageUrl={planImageUrl}
          />
        </Card>
      </Col>

      {/* 风水分析结果 */}
      <Col xs={24} lg={12}>
        <Card className="result-card insight-card" title="风水分析" bordered={false}>
          <div className="score-circle report-score">
            <div
              className="score-number"
              style={{ color: getScoreColor(fengShuiAnalysis.overallScore) }}
            >
              {fengShuiAnalysis.overallScore}
            </div>
            <div className="score-label">总体评分</div>
          </div>

          <Title level={4}>朝向点评</Title>
          <div className="mountain-note">
            <span>二十四山</span>
            坐{chart.sittingLabel}{chart.sittingMountainLabel} {Math.round(chart.sittingAngle)}°
            （{chart.sittingMountainMeta.yuanDragon}
            {chart.sittingMountainMeta.yinYang === 'yang' ? '阳' : '阴'}，
            {chart.sittingLineInfo.statusLabel}）
            · 向{chart.facingLabel}{chart.facingMountainLabel} {Math.round(chart.facingAngle)}°
            （{chart.facingMountainMeta.yuanDragon}
            {chart.facingMountainMeta.yinYang === 'yang' ? '阳' : '阴'}，
            {chart.facingLineInfo.statusLabel}）
          </div>
          <CommentaryFocusPanel focus={fengShuiAnalysis.orientationFocus} />

          {orientationComment.annualText && (
            <div className="annual-orientation-note">
              <strong>{annualYear} 年流年提示</strong>
              <Paragraph>{renderHighlightedImagery(orientationComment.annualText)}</Paragraph>
            </div>
          )}
          <CollapsibleComment
            title="朝向点评正文"
            text={orientationComment.orientationText || '暂无朝向点评。'}
          />

          <Title level={4}>格局点评</Title>
          <CommentaryFocusPanel focus={fengShuiAnalysis.layoutFocus} />
          <CollapsibleComment
            title="格局点评正文"
            text={fengShuiAnalysis.layoutComment || '暂无格局点评。'}
          />

          {fengShuiAnalysis.xingLuanAnalysis && (
            <Card
              size="small"
              className="xingluan-card"
              bordered={false}
              title={
                <span>
                  <EnvironmentOutlined />
                  形峦分析
                </span>
              }
            >
              <div className="xingluan-score">
                <strong>{fengShuiAnalysis.xingLuanAnalysis.score}</strong>
                <span>外局评分</span>
              </div>
              <Paragraph>{renderHighlightedImagery(fengShuiAnalysis.xingLuanAnalysis.formComment)}</Paragraph>
              <small>{renderHighlightedImagery(fengShuiAnalysis.xingLuanAnalysis.surroundingPattern)}</small>
              <Row gutter={[12, 12]} className="xingluan-lists">
                <Col xs={24} sm={8}>
                  <h5>外局优势</h5>
                  <List
                    size="small"
                    dataSource={fengShuiAnalysis.xingLuanAnalysis.advantages}
                    renderItem={(item) => <List.Item>• {renderHighlightedImagery(item)}</List.Item>}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <h5>注意点</h5>
                  <List
                    size="small"
                    dataSource={fengShuiAnalysis.xingLuanAnalysis.concerns}
                    renderItem={(item) => <List.Item>• {renderHighlightedImagery(item)}</List.Item>}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <h5>调整建议</h5>
                  <List
                    size="small"
                    dataSource={fengShuiAnalysis.xingLuanAnalysis.suggestions}
                    renderItem={(item) => <List.Item>• {renderHighlightedImagery(item)}</List.Item>}
                  />
                </Col>
              </Row>
              <Text type="secondary">
                数据来源：{fengShuiAnalysis.xingLuanAnalysis.dataSource}
              </Text>
            </Card>
          )}

          {fengShuiAnalysis.directionalGuidance && fengShuiAnalysis.directionalGuidance.length > 0 && (
            <>
              <Title level={4}>各方位宜忌</Title>
              <div className="direction-guidance-grid">
                {fengShuiAnalysis.directionalGuidance.map((item) => (
                  <div className="direction-guidance-card" key={item.direction}>
                    <h5>{item.direction}</h5>
                    <p>
                      <strong>忌</strong>
                      {renderHighlightedImagery(formatGuidanceList(item.avoid))}
                    </p>
                    <p>
                      <strong>宜</strong>
                      {renderHighlightedImagery(formatGuidanceList(item.prefer))}
                    </p>
                    <small>{renderHighlightedImagery(item.reason)}</small>
                  </div>
                ))}
              </div>
            </>
          )}

          <Row gutter={[16, 16]}>
            {fengShuiAnalysis.advantages.length > 0 && (
              <Col xs={24} sm={24}>
                <Card
                  size="small"
                  className="finding-card finding-card-good"
                  bordered={false}
                  title={
                    <span>
                      <CheckCircleOutlined />
                      优势
                    </span>
                  }
                >
                  <List
                    size="small"
                    dataSource={fengShuiAnalysis.advantages}
                    renderItem={(item) => <List.Item>• {renderHighlightedImagery(item)}</List.Item>}
                  />
                </Card>
              </Col>
            )}

            {fengShuiAnalysis.disadvantages.length > 0 && (
              <Col xs={24} sm={24}>
                <Card
                  size="small"
                  className="finding-card finding-card-warning"
                  bordered={false}
                  title={
                    <span>
                      <WarningOutlined />
                      不足
                    </span>
                  }
                >
                  <List
                    size="small"
                    dataSource={fengShuiAnalysis.disadvantages}
                    renderItem={(item) => <List.Item>• {renderHighlightedImagery(item)}</List.Item>}
                  />
                </Card>
              </Col>
            )}
          </Row>

          {fengShuiAnalysis.suggestions.length > 0 && (
            <Card
              className="suggestion-card"
              bordered={false}
              title={
                <span>
                  <BulbOutlined />
                  改进建议
                </span>
              }
            >
              <List
                dataSource={fengShuiAnalysis.suggestions}
                renderItem={(item) => <List.Item>• {renderHighlightedImagery(item)}</List.Item>}
              />
            </Card>
          )}

          <Divider className="soft-divider" />

          <Title level={5}>总结</Title>
          <Paragraph>{renderHighlightedImagery(fengShuiAnalysis.summary || '暂无总结。')}</Paragraph>
        </Card>
      </Col>
      {airflowAnalysis && (
        <Col xs={24}>
          <AirflowAssessment airflowAnalysis={airflowAnalysis} />
        </Col>
      )}
      </Row>
    </div>
  );
};

export default AnalysisResult;
